import { startTransition, useMemo, useState } from "react"

import { QueryClient, useQueryClient } from "@tanstack/react-query"
import { Navigate, type NavigateFunction, useNavigate } from "react-router-dom"

import { Option } from "$/models/option"
import { isNullOrUndefined } from "$/utils/gates"
import { getUserLanguage } from "$/utils/lang"

import { Button } from "@/3514/components"
import { EParticipantHeaderQueryKey } from "@/3514/entities/participant.header"
import { type TUserState, useUser } from "@/3514/hooks"
import { EUserRole } from "@/3514/types"
import { INDEX_URL } from "@/constants"
import { useModulesByParticipant, useSubmitEnrollment } from "@/entities/module"
import { type IUseTranslation, useTranslation } from "@/hooks"
import CareTeamLink from "@/shared/CareTeamLink"
import Select from "@/shared/select-default/Select"

import "./styles.scss"

export const SelfEnrollmentBlock = () => {
    const { user, refetch: refetchUser }: TUserState<EUserRole.Participant> = useUser<EUserRole.Participant>()
    const { t }: IUseTranslation = useTranslation()
    const navigate: NavigateFunction = useNavigate()

    const { data } = useModulesByParticipant({
        participantId: user?.id,
        sortLanguage: getUserLanguage(user)
    })
    const submitEnrollment = useSubmitEnrollment(user?.id)
    const [selectedModule, setSelectedModule] = useState(null)
    const [isBusy, setIsBusy] = useState(false)

    const queryClient: QueryClient = useQueryClient()

    const optionsList = useMemo(() => {
        const options =
            data?.map(module => {
                const isAssignedOrCompleted = module.isAssigned || module.isCompleted

                return new Option({
                    value: module.id,
                    label: isAssignedOrCompleted
                        ? `${module.titlePrefLang} (Already Assigned)`
                        : `${module.titlePrefLang}`,
                    disabled: isAssignedOrCompleted
                })
            }) ?? []

        return [new Option({ value: "", label: "---", disabled: true }), ...options]
    }, [data])

    const noEnrollments = user.enrollments.length === 0

    const onSelect = (module: Option) => {
        setSelectedModule(module.value)
    }

    const notSelectedModule = isNullOrUndefined(selectedModule)

    const onAssignClick = () => {
        setIsBusy(true)
        submitEnrollment.mutate(selectedModule, {
            onSuccess: async (): Promise<void> => {
                await refetchUser()
                await queryClient.invalidateQueries({ queryKey: EParticipantHeaderQueryKey.ModulesExcerpts() })
                startTransition((): void => void navigate(INDEX_URL))
            },
            onSettled: () => {
                setIsBusy(false)
            }
        })
    }

    return !user.isEligibleSelectModule ? (
        <Navigate to={INDEX_URL} replace />
    ) : (
        <section className="self-enrollment-block">
            <p className="mb-1 font-extrabold text-center">
                {/* @ts-expect-error TODO check translation key */}
                <span className="color-brand d-block d-md-inline text-center">{t("Important!")}</span>{" "}
                {noEnrollments
                    ? // @ts-expect-error TODO check translation key
                      t("You have no current module assigned. You must self-assign one now.")
                    : // @ts-expect-error TODO check translation key
                      t("Your next module is currently unassigned. You must self-assign one now.")}
            </p>
            <p className="mb-[40px] text-center">
                {noEnrollments
                    ? // @ts-expect-error TODO check translation key
                      t("Once you finish assigning the module, you will be able to start Phase 1")
                    : // @ts-expect-error TODO check translation key
                      t("Once you finish assigning the module, you will return to your usual Wiser homepage.")}
            </p>

            <div className="d-flex justify-content-center align-items-center w-100 mb-50 flex-column flex-md-row">
                {/* @ts-expect-error TODO check translation key */}
                <p className="mb-md-0 mr-md-3">{t("Select a Module:")}</p>
                {data?.length > 0 && (
                    <Select
                        onSelect={onSelect}
                        optionsList={optionsList}
                        variant="secondary"
                        backgroundWhite
                        className="self-enrollment-select"
                    />
                )}
            </div>

            <Button className="mt-[30px]" isDisabled={notSelectedModule} isBusy={isBusy} onClick={onAssignClick}>
                {/* @ts-expect-error TODO check translation key */}
                {t("Assign Module")}
            </Button>

            <CareTeamLink variant="brand" className="fs-14" />
        </section>
    )
}
